'use client';

import { useMemo } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Options } from '@contentful/rich-text-react-renderer';
import { Block, BLOCKS, type Document, INLINES, MARKS, Node, Text } from '@contentful/rich-text-types';
import renderEmbeddedEntry from '@/components/nodes/EmbeddedEntry';
import renderEntryHyperlink from '@/components/nodes/EntryHyperlink';
import renderEmbeddedAsset from '@/components/nodes/EmbeddedAsset';
import renderAssetHyperlink from '@/components/nodes/AssetHyperlink';
import Link from '@/components/shared/Link/Link';
import type { DocumentWithEmbeddedEntries } from '@/libs/contentful/types';
import { useDisclosureNumberingMap } from '@/contexts/DisclosureProvider';
import { extractTextFromNode, getIdFromText, getLetterLabel } from '@/utils/formatters';
import { renderSuperscript } from '@/utils/superscriptUtils';
import { AgreementTable } from './AgreementTable';
import {
  BELOW_REV_MARKER_RE,
  CENTER_BLOCK_RE,
  CENTER_MARKER_RE,
  getTocAnchor,
  TOC_HEADING_RE,
  TOC_MARKER_RE,
} from './consts';

interface AgreementRichTextProps {
  body: DocumentWithEmbeddedEntries;
  sectionTitle?: string;
  /** Applies GLBA model-form table shading; scoped to privacy-notice pages. */
  banded?: boolean;
}

// Matches a leading sequential number prefix (e.g. "1.", "14)") at the start
// of a paragraph's text content. WP authors on agreement pages routinely
// hand-numbered top-level sections as plain text ("1. Definitions…",
// "2. Termination…"); the migration script stores these verbatim, so the
// Contentful editor sees them as authored.
const LEADING_NUMBER_RE = /^\s*(\d+)[.)]\s*/;

// Block-level centering renders from `data.textAlign`. The Contentful editor
// can't author block alignment directly, so editors wrap a block's text in
// `[center]...[/center]`; applyCenterMarkers converts that marker into
// `data.textAlign` before render (see below).
function alignClass(node: Node): string {
  return node.data?.textAlign === 'center' ? ' text-center' : '';
}

interface LeadingNumber {
  number: number;
  textNode: Node;
  consumedLength: number;
}

function getLeadingNumberFromParagraph(node: Node): LeadingNumber | null {
  if (node.nodeType !== BLOCKS.PARAGRAPH) return null;
  const content = (node as Block).content;
  if (!Array.isArray(content)) return null;
  // Walk the start of the paragraph looking for the first text node with a
  // non-empty value. Skip empty text nodes (parser artifacts) but bail on
  // any non-text inline element — once an inline element opens, this isn't
  // a simple "N." prefix anymore.
  for (const child of content) {
    if (child.nodeType !== 'text') return null;
    const value = (child as Text).value;
    if (!value) continue;
    const match = value.match(LEADING_NUMBER_RE);
    if (!match) return null;
    return { number: parseInt(match[1], 10), textNode: child, consumedLength: match[0].length };
  }
  return null;
}

function stripLeadingNumberFromParagraph(paragraph: Node, leading: LeadingNumber): Node {
  const content = (paragraph as Block).content;
  if (!Array.isArray(content)) return paragraph;
  return {
    ...paragraph,
    content: content.map(child => {
      if (child !== leading.textNode) return child;
      const text = child as Text;
      return { ...child, value: text.value.substring(leading.consumedLength) };
    }),
  } as Node;
}

function isTocParagraph(node: Node): boolean {
  if (node.nodeType !== BLOCKS.PARAGRAPH) return false;
  return TOC_HEADING_RE.test(extractTextFromNode(node));
}

/**
 * Walks the document's top-level children. When two or more consecutive
 * paragraphs begin with sequential "N.", "N+1.", "N+2." … prefixes (any
 * starting N, not just 1), the WP author was hand-numbering top-level
 * sections. Group them into a synthetic `ordered-list` so the rendered
 * output is a real `<ol>` — each `list-item` swallows the lead paragraph
 * (with the "N." prefix stripped) plus any subsequent non-numbered content
 * up to the next sibling that opens a new item. The starting number is
 * carried on `data.start` so the renderer applies `<ol start="N">` when N>1
 * (a TOC sub-section break, or an out-of-sequence number, can split one
 * authored sequence into multiple OL segments). The Contentful entry stays
 * as plain paragraphs (no schema-side plumbing, no table-inside-li issue)
 * — only the rendered HTML gets list semantics.
 */
function wrapPlainNumberedParagraphs(content: Node[]): Node[] {
  const result: Node[] = [];
  let i = 0;
  while (i < content.length) {
    const node = content[i];
    const leading = getLeadingNumberFromParagraph(node);
    if (!leading) {
      result.push(node);
      i++;
      continue;
    }
    // Found a numbered paragraph — scan forward for a sequential successor.
    // Sub-section markers ([toc]…[/toc]) and any out-of-sequence numbered
    // paragraph end the run.
    const items: { paragraphIdx: number; leading: LeadingNumber }[] = [{ paragraphIdx: i, leading }];
    let scanIdx = i + 1;
    let expected = leading.number + 1;
    while (scanIdx < content.length) {
      const scanNode = content[scanIdx];
      if (isTocParagraph(scanNode)) break;
      const scanLeading = getLeadingNumberFromParagraph(scanNode);
      if (scanLeading) {
        if (scanLeading.number !== expected) break;
        items.push({ paragraphIdx: scanIdx, leading: scanLeading });
        expected++;
      }
      scanIdx++;
    }
    if (items.length < 2) {
      result.push(node);
      i++;
      continue;
    }
    // Trailing non-numbered, non-TOC content after the last numbered paragraph
    // gets absorbed into the last item — the WP author wrote those paragraphs
    // as continuation of the final section.
    let runEnd = scanIdx;
    while (runEnd < content.length) {
      const trailing = content[runEnd];
      if (isTocParagraph(trailing)) break;
      if (getLeadingNumberFromParagraph(trailing)) break;
      runEnd++;
    }
    const listItems: Node[] = items.map((entry, idx) => {
      const itemStart = entry.paragraphIdx;
      const itemEnd = idx + 1 < items.length ? items[idx + 1].paragraphIdx : runEnd;
      const stripped = stripLeadingNumberFromParagraph(content[itemStart], entry.leading);
      return {
        nodeType: BLOCKS.LIST_ITEM,
        data: {},
        content: [stripped, ...content.slice(itemStart + 1, itemEnd)],
      } as Node;
    });
    const startNumber = items[0].leading.number;
    result.push({
      nodeType: BLOCKS.OL_LIST,
      data: startNumber > 1 ? { start: startNumber } : {},
      content: listItems,
    } as Node);
    i = runEnd;
  }
  return result;
}

function getQuoteDepth(node: Node): number {
  const depth = node.data?.quoteDepth;
  // Narrows an unparsed value to a primitive before using it.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  return typeof depth === 'number' ? depth : 0;
}

/**
 * Re-nests paragraphs that were flattened out of nested blockquotes.
 *
 * Contentful can't store a blockquote inside a blockquote, so the migration
 * (see transform `flattenBlockquoteContent`) flattens inner quotes into the outer
 * quote's paragraph list, tagging each lifted paragraph with `data.quoteDepth`.
 * This rebuilds the nesting: a run of paragraphs deeper than the current level is
 * wrapped back into a `blockquote` node, recursively, so each level renders as its
 * own indented rule — matching the WP source. A blockquote whose paragraphs are
 * all at the base depth (the common case) is returned unchanged.
 */
function nestQuoteParagraphs(paras: Node[], depth: number): Node[] {
  const result: Node[] = [];
  let i = 0;
  while (i < paras.length) {
    if (getQuoteDepth(paras[i]) <= depth) {
      result.push(paras[i]);
      i++;
      continue;
    }
    let j = i;
    while (j < paras.length && getQuoteDepth(paras[j]) > depth) j++;
    result.push({
      nodeType: BLOCKS.QUOTE,
      data: {},
      content: nestQuoteParagraphs(paras.slice(i, j), depth + 1),
    } as Node);
    i = j;
  }
  return result;
}

function rebuildNestedQuotes(node: Node): Node {
  const content = (node as Block).content;
  if (!Array.isArray(content)) return node;
  const rebuiltChildren = content.map(rebuildNestedQuotes);
  const nextContent = node.nodeType === BLOCKS.QUOTE ? nestQuoteParagraphs(rebuiltChildren, 0) : rebuiltChildren;
  return { ...node, content: nextContent } as Node;
}

// Removes every [center]/[/center] marker from the text nodes of a subtree,
// preserving node structure and marks (so bold centered copy stays bold).
function stripCenterMarkers(nodes: Node[]): Node[] {
  return nodes.map(child => {
    if (child.nodeType === 'text') {
      const text = child as Text;
      return { ...child, value: text.value.replace(CENTER_MARKER_RE, '') };
    }
    const grandchildren = (child as Block).content;
    if (Array.isArray(grandchildren)) {
      return { ...child, content: stripCenterMarkers(grandchildren) };
    }
    return child;
  });
}

// Converts an editor-authored `[center]...[/center]` marker into block alignment:
// when a paragraph/heading's entire text is wrapped in the marker, strip the
// marker and set `data.textAlign` so alignClass centers the block. Recurses so
// centered blocks nested in quotes are handled too.
function applyCenterMarkers(node: Node): Node {
  const content = (node as Block).content;
  if (!Array.isArray(content)) return node;
  const rebuiltChildren = content.map(applyCenterMarkers);
  const isAlignable = node.nodeType === BLOCKS.PARAGRAPH || node.nodeType.startsWith('heading-');
  if (isAlignable && CENTER_BLOCK_RE.test(extractTextFromNode(node))) {
    return {
      ...node,
      data: { ...node.data, textAlign: 'center' },
      content: stripCenterMarkers(rebuiltChildren),
    } as Node;
  }
  return { ...node, content: rebuiltChildren } as Node;
}

const AgreementRichText = ({ body, sectionTitle, banded }: AgreementRichTextProps) => {
  const disclosureNumberingMap = useDisclosureNumberingMap();
  const sectionAnchor = sectionTitle ? getIdFromText(sectionTitle) : undefined;
  const tocAnchorCounts = new Map<string, number>();
  let tocHeadingIndex = 0;
  // Set when we hit an empty `[toc][/toc]` paragraph (untitled sub-section)
  // so the letter label inlines into the following paragraph instead of
  // rendering alone on its own line.
  let pendingUntitledSubLetter = false;

  // Group consecutive plain-text "N." paragraphs into a synthetic <ol> at
  // render time — see wrapPlainNumberedParagraphs. The source entry in
  // Contentful stays as plain paragraphs (what the WP author wrote).
  const wrappedJson = useMemo(() => {
    if (!body.json) return body.json;
    const numbered = wrapPlainNumberedParagraphs(body.json.content || []);
    const centered = numbered.map(applyCenterMarkers);
    return { ...body.json, content: centered.map(rebuildNestedQuotes) };
  }, [body.json]);

  const options: Options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: Node, children: React.ReactNode) => {
        const text = extractTextFromNode(node);
        const match = text.match(TOC_HEADING_RE);
        if (match) {
          // A marker is not a body paragraph, so any pending untitled letter
          // is dropped — it had no body to inline into.
          pendingUntitledSubLetter = false;
          const headingText = match[1];
          if (!headingText) {
            // Untitled sub-section — defer letter to the next paragraph so it
            // renders inline with the body content. Counter is incremented
            // when the letter is actually emitted. Without a sectionAnchor,
            // skip the letter to stay consistent with titled markers below.
            if (sectionAnchor) {
              pendingUntitledSubLetter = true;
            }
            return null;
          }
          if (!sectionAnchor) {
            return <p className={`my-4 last:mb-0${alignClass(node)}`}>{headingText}</p>;
          }

          const headingLabel = `${getLetterLabel(tocHeadingIndex)}. ${headingText}`;
          const anchor = getTocAnchor(sectionAnchor, headingText, tocAnchorCounts);
          tocHeadingIndex += 1;

          return (
            <p id={anchor} className={`font-sans font-semibold my-4 last:mb-0${alignClass(node)}`}>
              {headingLabel}
            </p>
          );
        }
        if (pendingUntitledSubLetter) {
          pendingUntitledSubLetter = false;
          const letter = getLetterLabel(tocHeadingIndex);
          tocHeadingIndex += 1;
          return (
            <p className={`my-4 last:mb-0${alignClass(node)}`}>
              <span className="font-sans font-semibold">{`${letter}. `}</span>
              {children}
            </p>
          );
        }
        // A table footnote / continuation lifted out of a list-item is indented
        // to align with the surrounding list items (see transform `tableContinuation`).
        const tableContinuation = node.data?.tableContinuation;
        return <p className={`my-4 last:mb-0${tableContinuation ? ' pl-10' : ''}${alignClass(node)}`}>{children}</p>;
      },
      [BLOCKS.HEADING_2]: (node: Node, children: React.ReactNode) => (
        <h2 className={`font-sans font-semibold text-heading-md my-8${alignClass(node)}`}>{children}</h2>
      ),
      [BLOCKS.HEADING_3]: (node: Node, children: React.ReactNode) => (
        <h3 className={`font-sans font-semibold text-heading-sm my-4${alignClass(node)}`}>{children}</h3>
      ),
      [BLOCKS.HEADING_4]: (node: Node, children: React.ReactNode) => (
        <h4 className={`font-sans font-semibold my-4${alignClass(node)}`}>{children}</h4>
      ),
      [BLOCKS.HEADING_5]: (node: Node, children: React.ReactNode) => (
        <h5 className={`font-sans font-semibold my-4${alignClass(node)}`}>{children}</h5>
      ),
      [BLOCKS.HEADING_6]: (node: Node, children: React.ReactNode) => (
        <h6 className={`font-sans font-semibold my-4${alignClass(node)}`}>{children}</h6>
      ),
      [BLOCKS.UL_LIST]: (_node: Node, children: React.ReactNode) => (
        <ul className="list-disc pl-10 my-4 [&_ul]:list-[circle] [&_ul]:pl-10 [&_ul_ul]:list-[square]">{children}</ul>
      ),
      [BLOCKS.OL_LIST]: (node: Node, children: React.ReactNode) => {
        // wrapPlainNumberedParagraphs sets `data.start = N` on synthetic OLs
        // when a run starts mid-sequence (e.g. after a sub-section break).
        // Narrows an unparsed value to a primitive before using it.
        // oxlint-disable-next-line anti-slop/no-runtime-typeof
        const start = typeof node.data?.start === 'number' && node.data.start > 1 ? node.data.start : undefined;
        return (
          <ol
            start={start}
            className="list-decimal pl-10 my-4 [&_ol]:list-[lower-alpha] [&_ol]:pl-10 [&_ol_ol]:list-[lower-roman] [&_ol_ol_ol]:list-[lower-alpha]">
            {children}
          </ol>
        );
      },
      [BLOCKS.LIST_ITEM]: (_node: Node, children: React.ReactNode) => <li className="my-1">{children}</li>,
      // WP renders these legal callouts ("IMPORTANT:", "NOTE:", etc.) as an indented
      // rule. Tailwind's reset strips the browser-default blockquote indent, so restore
      // it: a light-gray left rule + padding, matching the WP theme. Nested quotes
      // (rebuilt from `data.quoteDepth` in rebuildNestedQuotes) stack these, recreating
      // the multi-level indent. A quote lifted out of a list-item by the table hoist
      // (`tableContinuation`) is shifted right so its rule aligns with the surrounding
      // list content, matching where it sat nested in the WP source.
      [BLOCKS.QUOTE]: (node: Node, children: React.ReactNode) => (
        <blockquote
          className={`border-l-[5px] border-gray-200 pl-5 my-4${node.data?.tableContinuation ? ' ml-10' : ''}`}>
          {children}
        </blockquote>
      ),
      [BLOCKS.TABLE]: (node: Node) => <AgreementTable node={node as Block} renderOptions={options} banded={banded} />,
      [BLOCKS.HR]: () => <hr className="my-4 border-content-primary" />,
      [BLOCKS.EMBEDDED_ASSET]: (node: Node, children: React.ReactNode) => {
        return <div className="my-4">{renderEmbeddedAsset(node, children, body, false)}</div>;
      },
      [INLINES.HYPERLINK]: (node: Node, children: React.ReactNode) => (
        <Link href={node.data.uri} className="inline underline">
          {children}
        </Link>
      ),
      [INLINES.ASSET_HYPERLINK]: (node: Node, children: React.ReactNode) => {
        return renderAssetHyperlink(node, children, body);
      },
      [INLINES.ENTRY_HYPERLINK]: (node: Node, children: React.ReactNode) => {
        return renderEntryHyperlink(node, children, body);
      },
      [INLINES.EMBEDDED_ENTRY]: (node: Node, children: React.ReactNode) => {
        return renderEmbeddedEntry(node, children, body, disclosureNumberingMap);
      },
    },
    renderMark: {
      [MARKS.SUPERSCRIPT]: (children: React.ReactNode) => renderSuperscript(children),
    },
    renderText: (text: string) => {
      // Strip `[toc]`, `[/toc]`, and `[below-rev]` markers so they
      // don't leak into the rendered copy.
      const cleaned = text.replace(TOC_MARKER_RE, '').replace(BELOW_REV_MARKER_RE, '');
      return cleaned.split('\n').reduce<React.ReactNode[]>((acc, segment, i) => {
        if (i > 0) acc.push(<br key={`br-${i}`} />);
        acc.push(segment);
        return acc;
      }, []);
    },
  };

  if (!wrappedJson) return null;

  return documentToReactComponents(wrappedJson as Document, options);
};

export { AgreementRichText };
